// components/ReadAloudActionItem.jsx
import * as Sentry from "@sentry/react";
import { useRef, useState, useEffect, useCallback } from "react";
import { RxSpeakerLoud, RxPause } from "react-icons/rx";
import { ChatBubbleActionItem } from "./ChatBubble";
import { useDeepinfraTTS } from "../../services/tts/useDeepinfraTTS";
import { showErrorMessage } from "@minohealth/common/utils";

/**
 * @typedef {Object} ReadAloudActionItemProps
 * @property {boolean} isStreamingMessage
 * @property {string} messageText
 * @property {Object} ttsHook - The TTS hook instance from parent component
 */

export default function ReadAloudActionItem({ isStreamingMessage, messageText, ttsHook }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAutoPaused, setIsAutoPaused] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);

  // Refs for managing audio state
  const stateCheckIntervalRef = useRef(null);
  const abortControllerRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);

  // Use the passed TTS hook instead of creating a new one
  const {
    processSequentialTTS,
    playAudio,
    stopAudio,
    resumeAudio,
    getAudioState,
    stripMarkdown,
  } = ttsHook || useDeepinfraTTS({
    voice: "tara",
    enabled: true,
    debug: true,
  });

  // Audio handler for sentence completion - plays WAV blob using AudioManager
  const handleAudioComplete = useCallback(async (audioBlob) => {
    try {
      if (!audioBlob || audioBlob.size === 0) {
        return;
      }

      // Use AudioManager to play audio - this will automatically stop any previous audio
      const audio = playAudio(audioBlob, { autoPlay: true, volume: 1 });
      
      // Store reference for cleanup
      audioSourceRef.current = audio;

      // Update progress
      setPlaybackProgress(prev => prev + 1);

      // Wait for audio to finish before continuing
      await new Promise((resolve) => {
        audio.addEventListener('ended', () => {
          resolve();
        });

        audio.addEventListener('error', (error) => {
          resolve(); // Continue with next sentence even if this one fails
        });
      });

    } catch (error) {
      Sentry.captureException(error, {
        extra: { 
          context: 'ReadAloudActionItem - audio playback completion',
          audioBlobSize: audioBlob?.size,
          playbackProgress: playbackProgress,
          isPlaying
        }
      });
    }
  }, [playAudio]);

  // Enhanced audio state monitoring
  useEffect(() => {
    if (isPlaying) {
      stateCheckIntervalRef.current = setInterval(() => {
        const audioState = getAudioState();
        const wasAutopaused = !audioState.isPlaying && audioState.hasCurrentAudio && audioState.isSTTActive;

        setIsAutoPaused(wasAutopaused);
      }, 300);
    } else {
      if (stateCheckIntervalRef.current) {
        clearInterval(stateCheckIntervalRef.current);
        stateCheckIntervalRef.current = null;
      }
      setIsAutoPaused(false);
    }

    return () => {
      if (stateCheckIntervalRef.current) {
        clearInterval(stateCheckIntervalRef.current);
        stateCheckIntervalRef.current = null;
      }
    };
  }, [isPlaying, getAudioState]);

  // Main play function with sequential processing
  const handlePlay = useCallback(async () => {
    if (isPlaying) return handlePause();

    try {
      setIsPlaying(true);
      setIsAutoPaused(false);
      setPlaybackProgress(0);

      // Create abort controller for this session
      abortControllerRef.current = new AbortController();


      // Process sentences sequentially with audio playback
      await processSequentialTTS(
        messageText,
        handleAudioComplete,
        abortControllerRef.current.signal
      );


      // Auto-stop when complete
      setIsPlaying(false);

    } catch (error) {
      Sentry.captureException(error, {
        extra: { 
          context: 'ReadAloudActionItem - TTS playback process',
          messageTextLength: messageText?.length,
          isPlaying,
          playbackProgress,
          aborted: abortControllerRef.current?.signal?.aborted
        }
      });
      console.error("TTS: Error during playback:", error);
      if (error.name !== "AbortError") {
        showErrorMessage(`Failed to play audio: ${error.message}`);
      }
      handlePause();
    }
  }, [isPlaying, messageText, processSequentialTTS, handleAudioComplete]);

  // Pause function
  const handlePause = useCallback(() => {
    setIsPlaying(false);
    setIsAutoPaused(false);
    setPlaybackProgress(0);

    // Abort current processing
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Stop audio manager - this will handle stopping and cleanup
    stopAudio();
    
    // Clear audio reference
    audioSourceRef.current = null;
  }, [stopAudio]);

  // Resume function
  const handleResume = useCallback(() => {
    setIsAutoPaused(false);
    resumeAudio();
  }, [resumeAudio]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear intervals
      if (stateCheckIntervalRef.current) {
        clearInterval(stateCheckIntervalRef.current);
        stateCheckIntervalRef.current = null;
      }

      // Abort any ongoing processing
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Stop audio sources
      if (audioSourceRef.current) {
        try {
          if (audioSourceRef.current instanceof Audio) {
            audioSourceRef.current.pause();
            audioSourceRef.current.currentTime = 0;
          }
        } catch (error) {
          // Ignore errors
        }
      }
    };
  }, []);

  // Get button state
  const getButtonState = useCallback(() => {
    if (isAutoPaused) {
      return {
        title: "Resume audio",
        onClick: handleResume,
        icon: <RxSpeakerLoud />,
        disabled: false
      };
    }

    if (isPlaying) {
      return {
        title: `Stop audio (${playbackProgress} sentences)`,
        onClick: handlePause,
        icon: <RxPause />,
        disabled: false
      };
    }

    // Check if we have text to play
    const hasText = messageText && stripMarkdown(messageText.trim()).length > 0;

    return {
      title: hasText ? "Play audio (sequential streaming)" : "No text to play",
      onClick: hasText ? handlePlay : undefined,
      icon: <RxSpeakerLoud />,
      disabled: !hasText
    };
  }, [isAutoPaused, isPlaying, playbackProgress, messageText, stripMarkdown,
    handleResume, handlePause, handlePlay]);

  const buttonState = getButtonState();

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <ChatBubbleActionItem
        title={buttonState.title}
        onClick={buttonState.onClick}
        disabled={buttonState.disabled}
      >
        {buttonState.icon}
      </ChatBubbleActionItem>
    </div>
  );
}